import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import { getHalaqaInsights } from '../services/insightsService';
import Feature from './Feature';
import './InsightsWidget.css';

const InsightsWidget = ({ halaqaId, timeframe = 7 }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (halaqaId && user) {
      fetchInsights();
    }
  }, [halaqaId, timeframe, user]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingStartTime(Date.now());
      
      // Validate required props
      if (!halaqaId) {
        setError('No class selected');
        return;
      }
      
      if (!user) {
        setError('User authentication required');
        return;
      }
      
      // Check if user has appropriate role
      if (!['teacher', 'admin', 'super_admin'].includes(user.role)) {
        setError('You do not have permission to view insights');
        return;
      }

      console.log(`Fetching insights for halaqa: ${halaqaId}, timeframe: ${timeframe} days`);
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );
      
      const response = await Promise.race([
        getHalaqaInsights(halaqaId, timeframe),
        timeoutPromise
      ]);
      
      // Ensure minimum loading time of 800ms for better UX
      const elapsedTime = Date.now() - loadingStartTime;
      const minLoadingTime = 800;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Validate response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      if (response.success && response.data) {
        setInsights(response.data);
        console.log('Insights loaded successfully:', response.data);
      } else {
        setError(response.message || 'Failed to load insights');
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      
      // Handle different error types gracefully
      if (err.message === 'Request timeout') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view these insights');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Class not found or no longer exists');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to load insights. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchInsights();
  };

  // Format date range for display
  const formatDateRange = () => {
    if (!insights?.dateRange) return '';

    const startDate = new Date(insights.dateRange.start);
    const endDate = new Date(insights.dateRange.end);
    const today = new Date();

    const formatDate = (date) => {
      const month = date.toLocaleString('en', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };

    // FIX: Compare dates in UTC to determine if it's "today"
    const isToday =
      endDate.getUTCDate() === today.getUTCDate() &&
      endDate.getUTCMonth() === today.getUTCMonth() &&
      endDate.getUTCFullYear() === today.getUTCFullYear();

    const endDateStr = isToday ? 'Today' : formatDate(endDate);

    return `${formatDate(startDate)} - ${endDateStr}`;
  };

  // Don't render if user doesn't have appropriate role
  if (!user || !['teacher', 'admin', 'super_admin'].includes(user.role)) {
    return null;
  }

  // Don't render if no halaqa is selected
  if (!halaqaId) {
    return (
      <div className="insights-widget">
        <div className="insights-card">
          <h3 className="insights-title">📊 Class Insights</h3>
          <p className="insights-message">Select a class to view performance insights</p>
        </div>
      </div>
    );
  }

  // Loading skeleton component
  const LoadingSkeleton = ({ items = 2 }) => (
    <div className="loading-skeleton">
      {[...Array(items)].map((_, index) => (
        <div key={index} className="skeleton-item">
          <div className="skeleton-rank"></div>
          <div className="skeleton-name"></div>
          <div className="skeleton-badge"></div>
        </div>
      ))}
    </div>
  );

  // v1.0.2 Feature: Configuration for data insights limits
  const STUDENTS_NEEDING_SUPPORT_LIMIT = 3; // v1.0.1: Limit to top 3 students

  return (
    <div className="insights-widget">
      {/* Top Performers Card - Professional tier feature */}
      <Feature name="topPerformers">
        <div className="insights-card">
          <h3 className="insights-title">
            🏆 Top Performers
            {insights?.dateRange && (
              <span className="date-range">({formatDateRange()})</span>
            )}
            <span
              className="info-icon"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              ℹ️
              {showTooltip && (
                <div className="tooltip">
                  • New memorization assignments only<br/>
                  • Last 7 days: {formatDateRange()}<br/>
                  • Minimum 3 assessments required
                </div>
              )}
            </span>
          </h3>
          {loading ? (
            <LoadingSkeleton items={3} />
          ) : error ? (
            <div className="insights-error">
              <p>{error}</p>
              <button onClick={handleRetry} className="retry-btn">Try Again</button>
            </div>
          ) : insights?.insights?.topPerformers?.length > 0 ? (
            <div className="top-performers-list fade-in">
              {insights.insights.topPerformers.map((student, index) => (
                <div key={student.studentId} className="performer-item">
                  <span className="rank">{index + 1}</span>
                  <span className="student-name">{student.studentName}</span>
                  <span className="performance-badge">
                    {student.averageRating}/5.0 ({student.assessmentCount} assessments)
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="insights-message fade-in">No top performers in the last {timeframe} days</p>
          )}
        </div>
      </Feature>

      {/* Students Needing Support Card - Professional tier feature */}
      <Feature name="studentsNeedingSupport">
        <div className="insights-card">
          <h3 className="insights-title">⚠️ Students Needing Support</h3>
          {loading ? (
            <LoadingSkeleton items={2} />
          ) : error ? (
            <div className="insights-error">
              <p>{error}</p>
              <button onClick={handleRetry} className="retry-btn">Try Again</button>
            </div>
          ) : (
            <div className="support-list fade-in">
              {(() => {
                // v1.0.1: Combine and limit students needing support to top 3
                const missingWork = insights?.insights?.studentsNeedingSupport?.missingWork || [];
                const failingAssessments = insights?.insights?.studentsNeedingSupport?.failingAssessments || [];
                
                // Combine all students needing support (missing work has higher priority)
                const allStudentsNeedingSupport = [
                  ...missingWork.map(student => ({ ...student, type: 'critical' })),
                  ...failingAssessments.map(student => ({ ...student, type: 'warning' }))
                ];
                
                // Limit to STUDENTS_NEEDING_SUPPORT_LIMIT (3 for v1.0.1)
                const limitedStudents = allStudentsNeedingSupport.slice(0, STUDENTS_NEEDING_SUPPORT_LIMIT);
                
                return (
                  <>
                    {/* Display limited students */}
                    {limitedStudents.map((student) => (
                      <div key={student.studentId} className={`support-item ${student.type}`}>
                        <span className="student-name">{student.studentName}</span>
                        <span className="support-detail">{student.detail}</span>
                      </div>
                    ))}

                    {/* Show count if there are more students */}
                    {allStudentsNeedingSupport.length > STUDENTS_NEEDING_SUPPORT_LIMIT && (
                      <div className="support-item-summary">
                        <span className="summary-text">
                          ... and {allStudentsNeedingSupport.length - STUDENTS_NEEDING_SUPPORT_LIMIT} more student{allStudentsNeedingSupport.length - STUDENTS_NEEDING_SUPPORT_LIMIT > 1 ? 's' : ''} needing support
                        </span>
                      </div>
                    )}

                    {/* No students needing support */}
                    {allStudentsNeedingSupport.length === 0 && (
                      <p className="insights-message">✅ All students are performing well!</p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </Feature>
    </div>
  );
};

export default InsightsWidget;